import { ChevronRight } from 'lucide-react';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

/**
 * Gập/mở dàn ý theo từng mục và theo cấp — dùng chung cho cột dàn ý của trình
 * soạn (`editor/outline.tsx`) và của bản xem trước (`preview/viewer-controls.tsx`).
 *
 * Mô hình: cha của một mục là mục **gần nhất phía trên có cấp nhỏ hơn**, nên
 * tiêu đề nhảy cấp (H1 rồi H3) vẫn ra cây đúng. Trạng thái gồm hai phần:
 *
 * - `depth` — "hiện tới cấp N" (nút 1 2 3… ở đầu cột). Mục có cấp > N bị ẩn,
 *   trừ khi người dùng đã tự mở cha của nó.
 * - `overrides` — người dùng bấm mũi tên ở một mục. Chọn lại cấp thì xoá hết.
 *
 * Khoá của mục là `cấp:chữ#thứ-tự-trùng`, không phải vị trí: ở trình soạn vị trí
 * đổi theo từng phím gõ, và một mục đang gập không được tự bung ra chỉ vì người
 * ta gõ thêm một chữ ở đoạn phía trên.
 */

export interface FoldEntry {
  readonly level: number;
  readonly text: string;
}

export interface FoldState {
  /** Cấp sâu nhất còn hiện; `Infinity` = mọi cấp. */
  readonly depth: number;
  readonly overrides: ReadonlyMap<string, boolean>;
}

export const ALL_LEVELS: FoldState = { depth: Infinity, overrides: new Map() };

/** Khoá ổn định của từng mục — tiêu đề trùng chữ đánh số theo lần xuất hiện. */
export function foldKeysOf(entries: readonly FoldEntry[]): string[] {
  const seen = new Map<string, number>();
  return entries.map(({ level, text }) => {
    const base = `${level}:${text}`;
    const nth = seen.get(base) ?? 0;
    seen.set(base, nth + 1);
    return `${base}#${nth}`;
  });
}

/** Chỉ số của cha mỗi mục (`-1` = gốc): mục gần nhất phía trên có cấp nhỏ hơn. */
export function parentsOf(entries: readonly FoldEntry[]): number[] {
  const stack: number[] = [];
  return entries.map((entry, index) => {
    while (stack.length && entries[stack.at(-1)!]!.level >= entry.level) stack.pop();
    const parent = stack.at(-1) ?? -1;
    stack.push(index);
    return parent;
  });
}

/** Các cấp có mặt, tăng dần — mỗi cấp một nút "hiện tới cấp". */
export function levelsOf(entries: readonly FoldEntry[]): number[] {
  return [...new Set(entries.map((entry) => entry.level))].sort((a, b) => a - b);
}

export interface FoldView {
  readonly visible: readonly boolean[];
  readonly hasChildren: readonly boolean[];
  /** Có ít nhất một mục con đang hiện — mũi tên chỉ xuống. */
  readonly expanded: readonly boolean[];
  readonly parents: readonly number[];
}

/** Hàm thuần: mục nào hiện, mục nào có con, mục nào đang mở. */
export function foldView(
  entries: readonly FoldEntry[],
  keys: readonly string[],
  state: FoldState,
): FoldView {
  const parents = parentsOf(entries);
  const hasChildren = entries.map(() => false);
  for (const parent of parents) if (parent >= 0) hasChildren[parent] = true;
  const open = entries.map(
    (entry, index) => state.overrides.get(keys[index]!) ?? entry.level < state.depth,
  );
  const visible: boolean[] = [];
  const expanded = entries.map(() => false);
  entries.forEach((entry, index) => {
    const parent = parents[index]!;
    if (parent < 0) {
      visible.push(true);
      return;
    }
    // Cha do người dùng tự mở ⇒ hiện mọi con, kể cả con sâu hơn `depth`.
    const shown =
      visible[parent]! &&
      open[parent]! &&
      (state.overrides.has(keys[parent]!) || entry.level <= state.depth);
    visible.push(shown);
    if (shown) expanded[parent] = true;
  });
  return { visible, hasChildren, expanded, parents };
}

/** Mục đang hiện gần nhất chứa `index` — mục đang đọc nằm trong nhánh gập thì tô cha nó. */
export function visibleOwnerOf(view: FoldView, index: number): number {
  let at = index;
  while (at >= 0 && !view.visible[at]) at = view.parents[at]!;
  return at;
}

/** Các mục thuộc nhánh của `index` (không gồm chính nó). */
export function descendantsOf(entries: readonly FoldEntry[], index: number): number[] {
  const level = entries[index]!.level;
  const result: number[] = [];
  for (let at = index + 1; at < entries.length && entries[at]!.level > level; at += 1) {
    result.push(at);
  }
  return result;
}

export interface OutlineFold {
  readonly view: FoldView;
  readonly levels: readonly number[];
  readonly depth: number;
  /** Có cây để gập không — không thì khỏi vẽ nút cấp và mũi tên (pptx: chỉ có slide). */
  readonly foldable: boolean;
  readonly toggle: (index: number) => void;
  readonly showLevel: (level: number) => void;
}

export function useOutlineFold(entries: readonly FoldEntry[]): OutlineFold {
  const [state, setState] = useState<FoldState>(ALL_LEVELS);
  const keys = useMemo(() => foldKeysOf(entries), [entries]);
  const view = useMemo(() => foldView(entries, keys, state), [entries, keys, state]);
  const levels = useMemo(() => levelsOf(entries), [entries]);
  const toggle = useCallback(
    (index: number) => {
      const key = keys[index];
      if (key === undefined) return;
      setState((current) => {
        const overrides = new Map(current.overrides);
        overrides.set(key, !view.expanded[index]);
        return { ...current, overrides };
      });
    },
    [keys, view],
  );
  const showLevel = useCallback((level: number) => {
    setState({ depth: level, overrides: new Map() });
  }, []);
  // Cấp sâu nhất = mọi cấp: nút cuối luôn là "mở hết".
  const depth = state.depth >= (levels.at(-1) ?? 0) ? Infinity : state.depth;
  return {
    view,
    levels,
    depth,
    foldable: view.hasChildren.some(Boolean),
    toggle,
    showLevel,
  };
}

/**
 * Hàng nút "hiện tới cấp N" — nút cuối là mọi cấp. Nhãn là **thứ hạng** của cấp
 * (1, 2, 3…), không phải số `H` thật: tài liệu chỉ có H2/H3 thì hai nút 1 và 2.
 */
export function OutlineLevelBar({ fold }: { readonly fold: OutlineFold }): ReactNode {
  const { t } = useTranslation();
  if (!fold.foldable) return null;
  return (
    <div
      role="group"
      aria-label={t('myFiles.outline.levels')}
      className="flex shrink-0 items-center gap-0.5"
    >
      {fold.levels.map((level, rank) => {
        const last = rank === fold.levels.length - 1;
        const pressed = last ? fold.depth === Infinity : fold.depth === level;
        const label = last
          ? t('myFiles.outline.showAll')
          : t('myFiles.outline.showLevel', { n: rank + 1 });
        return (
          <button
            key={level}
            type="button"
            aria-pressed={pressed}
            title={label}
            aria-label={label}
            onClick={() => fold.showLevel(last ? Infinity : level)}
            className={cn(
              'flex size-5 items-center justify-center rounded-DEFAULT text-[11px] font-medium tabular-nums',
              pressed
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {rank + 1}
          </button>
        );
      })}
    </div>
  );
}

/** Mũi tên gập/mở của một mục; mục không có con thì chừa chỗ để chữ thẳng hàng. */
export function OutlineChevron({
  fold,
  index,
  title,
}: {
  readonly fold: OutlineFold;
  readonly index: number;
  readonly title: string;
}): ReactNode {
  const { t } = useTranslation();
  if (!fold.foldable) return null;
  if (!fold.view.hasChildren[index]) return <span aria-hidden className="size-4 shrink-0" />;
  const expanded = fold.view.expanded[index]!;
  const label = expanded
    ? t('myFiles.outline.collapse', { title })
    : t('myFiles.outline.expand', { title });
  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      onClick={() => fold.toggle(index)}
      className="flex size-4 shrink-0 items-center justify-center rounded-DEFAULT text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      <ChevronRight className={cn('size-3.5 transition-transform', expanded && 'rotate-90')} />
    </button>
  );
}
